/**
 * Typy wniosków portalu — ekran pod kaflem „Słowniki" (fala 8, grupa 37; D2, D97).
 *
 * D2 zakładał, że firma konfiguruje kafelki portalu, ale zapis istniał tylko
 * przy zakładaniu obszaru — ani trasy, ani ekranu. Makieta kończy kafel
 * „Słowniki" toastem; tu otwiera listę typów z przełącznikami. Kafelek ustawowy
 * (członek rodziny, art. 67 ust. 3 u.ś.o.z.) i kafelki zmiany danych są zawsze
 * włączone i zablokowane — mówi o tym nota przy wierszu. Wyłączenie typu nie
 * rusza wniosków już złożonych.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  Checkbox,
  EmptyState,
  InfoBanner,
  Spinner,
  StatusPill,
  useToast,
} from '@k1p4/ui'
import type { PortalRequestType } from '@k1p4/shared'
import { ApiError, api } from '../../api/client'
import styles from './RequestTypesSettings.module.css'

export function RequestTypesSettings() {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  /* Zmiany lokalne nad stanem z serwera — zapis wysyła całą listę w kolejności. */
  const [draft, setDraft] = useState<Partial<Record<PortalRequestType, boolean>>>({})
  const [error, setError] = useState<string | null>(null)

  const types = useQuery({
    queryKey: ['settings', 'request-types'],
    queryFn: () => api.settingsRequestTypes(),
  })

  const save = useMutation({
    mutationFn: () =>
      api.saveSettingsRequestTypes(
        (types.data?.items ?? []).map((item) => ({
          type: item.type,
          enabled: draft[item.type] ?? item.enabled,
        })),
      ),
    onSuccess: () => {
      toast.show(t('requestTypesSettings.saved'), { tone: 'success' })
      setDraft({})
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['settings', 'request-types'] })
      void queryClient.invalidateQueries({ queryKey: ['request-types'] })
      void queryClient.invalidateQueries({ queryKey: ['settings', 'tiles'] })
    },
    onError: (caught) => {
      setError(
        caught instanceof ApiError && caught.code === 'STATUTORY_TYPE'
          ? 'requestTypesSettings.errorStatutory'
          : caught instanceof ApiError && caught.code === 'ALWAYS_ON_TYPE'
            ? 'requestTypesSettings.errorAlwaysOn'
            : 'requestTypesSettings.errorGeneric',
      )
    },
  })

  if (types.isPending) return <Spinner label={t('common.loading')} />
  if (types.isError) {
    return <EmptyState title={t('common.error')} description={t('common.retry')} />
  }
  const dirty = Object.keys(draft).length > 0

  return (
    <>
      <p className={styles.lead}>{t('requestTypesSettings.lead')}</p>
      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}
      <Card pad="flush" className={styles.list}>
        {types.data.items.map((item) => {
          const enabled = draft[item.type] ?? item.enabled
          return (
            <div key={item.type} className={styles.row}>
              <Checkbox
                label={t(`requestTypes.${item.type}`)}
                checked={enabled}
                disabled={item.locked !== null}
                onChange={(event) => {
                  const next = event.target.checked
                  setDraft((current) => {
                    const { [item.type]: _drop, ...rest } = current
                    return next === item.enabled ? rest : { ...rest, [item.type]: next }
                  })
                }}
              />
              <StatusPill tone={item.locked !== null ? 'pink' : 'neutral'}>
                {t(`requestTypesSettings.kinds.${item.locked ?? item.kind}`)}
              </StatusPill>
              {item.locked !== null ? (
                <p className={styles.note}>{t(`requestTypesSettings.lockedNote.${item.locked}`)}</p>
              ) : null}
            </div>
          )
        })}
      </Card>
      <div className={styles.actions}>
        <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? <Spinner label={t('requestTypesSettings.saving')} /> : t('requestTypesSettings.save')}
        </Button>
      </div>
    </>
  )
}
